import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfigProvider } from 'antd';
import LoadingSpinner from '../components/LoadingSpinner';
import * as _ from 'lodash';
import * as services from '../services';
import Cookies from 'js-cookie';
import { COOKIE_NAMES, GROUP_ACCOUNT_TYPES, LOGOUT_BROADCAST_KEY } from '../constants';
import http from '../services/user';
import httpFile from '../services/file';
import httpGroup from '../services/ping-group';
import httpPayment from '../services/payment';
import httpPaymentV2 from '../services/payment-v2';
import { getErrorMessage } from '../utils';
import { UserHelper } from '../helpers/user';
import { produce } from 'immer';

import { createContext, useContextSelector } from 'use-context-selector';
import { useHistory } from 'react-router-dom';
import { Business } from '../types/business';
import viVN from 'antd/lib/locale/vi_VN';
import enUS from 'antd/lib/locale/en_US';
import { SNotification } from '../services/notification';
import { LanguageModule } from '../locales';
import { updateUserProfile } from '../services';
import { User, UserType, ServiceType } from '../types/user';
import { Config } from '../config';
import { AxiosUtil } from '../utils/axios';

export type CurrentUser = {
  _id: string;
  email: string;
  phoneNumber: string;
  phoneNumberUserInput: string;
  phoneNumberCountryCode: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  businessWebsite?: string;
  businessName?: string;
  businessAddress?: string;
  businessCountry?: string;
  businessLocalLevel1?: string;
  businessLocalLevel2?: string;
  businessPostalCode?: string | number;

  businesses: Array<Business>;
  families: Array<any>;
};

export type AppContextType = {
  language: string;
  updateLanguage: (l: string) => void;
  setIsLoading: (show: boolean) => void;
  isLoading: boolean;
  initUserInfo: (data: { refreshToken: string; accessToken: string }) => Promise<void>;
  clearUserInfo: () => void;
  reloadCurrentUser: (showLoading?: boolean) => void;
  currentUser?: User | undefined;
  updateGroup: (data: { groupId: string; groupType: string; group: any }) => void;
  updateSubscription: (s: any) => void;
  reloadTree: () => void;
  currentSubscriptions: Array<any>;
  setCurrentSubscriptions: (s: Array<any>) => void;
  clearSessionAndRedirectToLogin: () => void;
  broadcastLogout: () => void;
};

// @ts-ignore
export const AppContext = createContext<AppContextType>({});

// eslint-disable-next-line import/no-anonymous-default-export
export default ({ children }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [reloadingTree, setReloadingTree] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>();
  const [language, setLanguage] = useState<string>(window.localStorage.getItem('language') || 'en');
  const [currentSubscriptions, setCurrentSubscriptions] = useState<any[]>([]);
  const refreshTokenTimerRef = useRef<any>();
  const history = useHistory();

  const reloadTree = useCallback(() => {
    setReloadingTree(true);
    setTimeout(() => {
      setReloadingTree(false);
    }, 250);
  }, []);

  const updateLanguage = useCallback(
    async (newLanguage: string) => {

      window.localStorage.setItem('language', newLanguage);
      LanguageModule.changeLanguage(newLanguage);
      setLanguage(newLanguage);
      reloadTree(); // --> do not updateLanguage on did mount

      if (!!currentUser) {
        const res = await updateUserProfile({ language: newLanguage });
      }
    },
    [currentUser],
  );

  const updateSubscription = useCallback(
    (subscription) => {
      setCurrentSubscriptions(
        produce<any[]>(currentSubscriptions, (draft) => {
          const subscriptionId = subscription.stripeSubscriptionId || subscription.id;
          const existIndex = draft.findIndex((s) => (s.stripeSubscriptionId || s.id) === subscriptionId);
          if (existIndex >= 0) draft[existIndex] = subscription;
          else draft.push(subscription);
        }),
      );
    },
    [currentUser],
  );

  const updateGroup = useCallback(
    ({ groupId, groupType, group } = {}) => {
      setCurrentUser(
        produce<any>(currentUser, (draft: any) => {
          const groups = groupType === GROUP_ACCOUNT_TYPES.BUSINESS ? draft.businesses : draft.families;
          const updateIndex = groups.findIndex((group: any) => group._id === groupId);
          if (updateIndex !== -1) {
            groups[updateIndex] = group;
          } else {
            groups.push(group);
          }
        }),
      );
    },
    [currentUser],
  );

  const isLoggedIn = !!currentUser?._id;

  const updateToken = useCallback(({ refreshToken, accessToken }) => {
    const cookieOptions = { expires: 30 };
    AxiosUtil.resetSessionState();
    Cookies.set(COOKIE_NAMES.COOKIE_REFRESH_TOKEN, refreshToken, cookieOptions);
    Cookies.set(COOKIE_NAMES.COOKIE_ACCESS_TOKEN, accessToken, cookieOptions);
    //Cookies.set(COOKIE_NAMES.COOKIE_EXPIRES_IN, _.get(data, 'accessTokenExpiresIn', '').toString());
    http.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    httpFile.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    httpGroup.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    httpPayment.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
    httpPaymentV2.defaults.headers['Authorization'] = `Bearer ${accessToken}`;
  }, []);

  // call on logout
  const clearUserInfo = useCallback(() => {
    AxiosUtil.resetSessionState();
    setCurrentUser(null);
    setCurrentSubscriptions([]);
    Cookies.set(COOKIE_NAMES.COOKIE_REFRESH_TOKEN, '');
    Cookies.set(COOKIE_NAMES.COOKIE_ACCESS_TOKEN, '');
    Cookies.set(COOKIE_NAMES.COOKIE_EXPIRES_IN, '');
  }, [setCurrentUser]);

  const clearSessionAndRedirectToLogin = useCallback(() => {
    clearUserInfo();
    history.push('/login');
  }, [clearUserInfo, history]);

  // Notify other tabs of this browser that the user logged out, so they log out too instead of staying usable.
  const broadcastLogout = useCallback(() => {
    try {
      window.localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    } catch (error) {
      // localStorage unavailable (e.g. private browsing) — cross-tab sync is best-effort, ignore.
    }
  }, []);

  // React to a logout broadcast from another tab. Only reacts (never re-broadcasts) to avoid an infinite loop.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === LOGOUT_BROADCAST_KEY && event.newValue) {
        clearSessionAndRedirectToLogin();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [clearSessionAndRedirectToLogin]);

  const reloadCurrentUser = useCallback(
    async (showLoading = true) => {
      try {
        if (showLoading) setIsLoading(true);
        const me = await services.getCurrentUser();
        const user = me.data;
        if (user) setCurrentUser(user);
        if (Config.featureFlag.requireSubscription) {
          const res = await services.getStripeSubscriptions();
          setCurrentSubscriptions(res.data?.subscriptions || []);
        } else {
          setCurrentSubscriptions([]);
        }

        return user;
      } catch (error) {
        setCurrentSubscriptions([]);
        throw error;
      } finally {
        if (showLoading) setIsLoading(false);
      }
    },
    [setCurrentUser, setIsLoading, language],
  );

  // call on login
  const initUserInfo = useCallback(
    async ({ refreshToken, accessToken }) => {
      try {
        updateToken({
          refreshToken,
          accessToken,
        });
        const user = await reloadCurrentUser(false);
        window.localStorage.setItem('language', user.language);
        LanguageModule.changeLanguage(user.language);
        setLanguage(user.language);
      } catch (error) {
        clearUserInfo();
        throw error;
      }
    },
    [updateToken, reloadCurrentUser, clearUserInfo],
  );

  useEffect(() => {
    (async () => {
      setInitialized(false);
      try {
        const refreshToken = Cookies.get(COOKIE_NAMES.COOKIE_REFRESH_TOKEN);
        if (!refreshToken) {
          clearUserInfo();
        } else {
          await reloadCurrentUser();
        }
      } catch (error: any) {
        const errorStatus = error?.response?.status;
        if (errorStatus === 401 || errorStatus === 403) {
          // clearUserInfo();
        }
        SNotification.error(getErrorMessage(error));
      }
      setInitialized(true);
    })();
  }, []);

  return (
    <ConfigProvider locale={language === 'vi' ? viVN : enUS}>
      <AppContext.Provider
        value={{
          language,
          updateLanguage,
          setIsLoading,
          isLoading,
          reloadCurrentUser,
          currentUser,
          updateGroup,
          updateSubscription,
          currentSubscriptions,
          setCurrentSubscriptions,
          clearSessionAndRedirectToLogin,
          broadcastLogout,
          reloadTree,
          initUserInfo,
          clearUserInfo,
        }}
      >
        {!initialized ? (
          <LoadingSpinner />
        ) : (
          <>
            {children}
            {/* Overlays instead of unmounting: keeps routes/modals mounted (e.g. Add Card + Braintree). */}
            {(isLoading || reloadingTree) && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 10050,
                }}
              >
                <LoadingSpinner />
              </div>
            )}
          </>
        )}
      </AppContext.Provider>
    </ConfigProvider>
  );
};
